import React, { useCallback, useState } from "react";
import "../Quiz/Quiz.css";
import "./SoccerGame.css";
import { Seo } from "../../Shared/Seo/Seo.tsx";
import { IntroLead, IntroRest } from "../../Landing/IntroParagraphs.tsx";
import { findBySlug, quizRoutes } from "../../Shared/Seo/routeSeo.ts";
import { Operations } from "../../MathFactsGenerator/Constants.ts";
import { SettingsProvider } from "../../MathFactsGenerator/Settings/SettingsStore.tsx";
import { ValueRange } from "../../MathFactsGenerator/Settings/ValueRange.tsx";
import { canBuildDeck, flashCardSettings } from "../deck.ts";
import { createQuizQueue, QuizQueue } from "../Quiz/quizQueue.ts";
import { readBestScore, recordScore, soccerBestScoreKey } from "../Quiz/bestScore.ts";
import { factKey, recordCorrect, recordMiss } from "../Quiz/missedFacts.ts";
import { MissedFactsPractice } from "../Quiz/MissedFactsPractice.tsx";
import { track } from "../../Shared/analytics.ts";
import { SoccerGameRun, SoccerResult } from "./SoccerGameRun.tsx";
import { SoccerGameSummary } from "./SoccerGameSummary.tsx";
import { goalsOf, Keeper, KEEPER_READY_MS, KEEPERS } from "./soccerGame.ts";
import { soccerGameEventParams } from "./gameEventParams.ts";
import { FactPicker, operationsOf } from "./FactPicker.tsx";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";
import { format } from "../../Shared/i18n/format.ts";
import { StringKey } from "../../Shared/i18n/strings.ts";

interface SoccerGamePageProps {
    slug: string;
    operation: Operations;
}

interface Summary {
    result: SoccerResult;
    best: number | null;
    isNewBest: boolean;
}

/** Each keeper's name. `labelKey` so StringKeys.test.js sees the keys, as QuizPage's modes do. */
const KEEPER_LABELS: Record<Keeper, { labelKey: StringKey }> = {
    rookie: { labelKey: "flashCards.soccerKeeperRookie" },
    pro: { labelKey: "flashCards.soccerKeeperPro" },
    legend: { labelKey: "flashCards.soccerKeeperLegend" },
};

/**
 * Math Facts Soccer (ADR 0059): a keyword landing page around a penalty
 * shootout on the quiz's queue, marking and stores. A quiz route carrying
 * `game: "soccer"`; its `quiz` is only the operation the picker opens on.
 */
export function SoccerGamePage({ slug, operation }: SoccerGamePageProps) {
    const entry = findBySlug(slug)!;
    const t = useTranslation();
    const [settings, setSettings] = useState(() => flashCardSettings(operation));
    const [keeper, setKeeper] = useState<Keeper | null>(null);
    const [queue, setQueue] = useState<QuizQueue | null>(null);
    const [runId, setRunId] = useState(0);
    const [summary, setSummary] = useState<Summary | null>(null);
    const [missesVersion, setMissesVersion] = useState(0);

    const ready = canBuildDeck(settings);
    const playing = keeper !== null && queue !== null && summary === null;
    const plainQuiz = quizRoutes().find((route) => route.quiz === operation && !route.game);
    // Captured at kick-off, so a result is keyed by what was actually played.
    const [playedOperations, setPlayedOperations] = useState<string[]>([]);

    function kickOff(chosen: Keeper) {
        // Built here from settings as they stand, never during render: the
        // generator is non-deterministic (ADR 0001 §4).
        setQueue(createQuizQueue(settings));
        setPlayedOperations(operationsOf(settings));
        setKeeper(chosen);
        setSummary(null);
        setRunId((id) => id + 1);
    }

    function changeKeeper() {
        setKeeper(null);
        setQueue(null);
        setSummary(null);
    }

    const handleFinish = useCallback(
        (result: SoccerResult) => {
            const goals = goalsOf(result.kicks);
            const key = soccerBestScoreKey(playedOperations, result.keeper);
            const previous = readBestScore(key);
            const best = recordScore(key, goals);
            // A save was a right answer, only slow: it clears like a goal (§6).
            result.kicks.forEach((k) => {
                if (k.outcome === "wide" || k.outcome === "skipped") {
                    recordMiss(factKey(k.front));
                } else {
                    recordCorrect(factKey(k.front));
                }
            });
            setMissesVersion((v) => v + 1);
            // After the stores, as on the quiz: analytics never costs a best score.
            track("quiz_completed", soccerGameEventParams(playedOperations, result.kicks));
            setSummary({
                result,
                best: best > 0 ? best : null,
                isNewBest: previous !== null ? goals > previous : goals > 0,
            });
        },
        [playedOperations]
    );

    return (
        <SettingsProvider settings={settings} setSettings={setSettings}>
            <Seo entry={entry} />
            <div className="landingContent">
                <h1>{entry.h1}</h1>
                <IntroLead paragraphs={entry.intro} />
            </div>

            <div className="quizTool">
                {summary ? (
                    <SoccerGameSummary
                        result={summary.result}
                        best={summary.best}
                        isNewBest={summary.isNewBest}
                        onPlayAgain={() => kickOff(summary.result.keeper)}
                        onChangeKeeper={changeKeeper}
                    />
                ) : playing ? (
                    <SoccerGameRun key={runId} queue={queue!} keeper={keeper!} onFinish={handleFinish} />
                ) : (
                    <div className="quizModePicker soccerIntro">
                        <p className="soccerIntroHow">{t("flashCards.soccerHowToPlay")}</p>
                        <p className="quizModePrompt">{t("flashCards.soccerWhichFacts")}</p>
                        <FactPicker settings={settings} setSettings={setSettings} label={t("flashCards.soccerWhichFacts")} />
                        <p className="quizModePrompt">{t("flashCards.soccerChooseKeeper")}</p>
                        {ready ? (
                            <div className="soccerKeepers">
                                {KEEPERS.map((option) => (
                                    <button key={option} type="button" onClick={() => kickOff(option)}>
                                        <span className="soccerKeeperName">{t(KEEPER_LABELS[option].labelKey)}</span>
                                        <span className="soccerKeeperTime">
                                            {format(t("flashCards.soccerKeeperSecondsTemplate"), {
                                                n: KEEPER_READY_MS[option] / 1000,
                                            })}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <p className="quizEmpty">{t("flashCards.gameEmptyNote")}</p>
                        )}
                    </div>
                )}

                {!playing && plainQuiz && (
                    <p className="soccerCalmer">
                        {t("flashCards.gameCalmerPrefix")}
                        <a href={plainQuiz.slug}>{t("flashCards.gameCalmerLink")}</a>
                        {t("flashCards.gameCalmerSuffix")}
                    </p>
                )}

                {!playing && <MissedFactsPractice refreshKey={missesVersion} />}

                {!playing && (
                    <details className="quizSettings">
                        <summary>{t("flashCards.changeWhatToPractise")}</summary>
                        <div className="valueRangeContainer">
                            <ValueRange RangeSettings={0} />
                            <ValueRange RangeSettings={1} />
                        </div>
                    </details>
                )}
            </div>

            <IntroRest paragraphs={entry.intro} />

            {entry.faq && entry.faq.length > 0 && (
                <section className="landingFaq">
                    <h2>{t("flashCards.frequentlyAskedQuestions")}</h2>
                    {entry.faq.map((item, i) => (
                        <div key={i} className="faqItem">
                            <h3>{item.q}</h3>
                            <p>{item.a}</p>
                        </div>
                    ))}
                </section>
            )}
        </SettingsProvider>
    );
}

export default SoccerGamePage;
